import EditActivity from './EditActivity';
import EditModules from './EditModules';
import EditBiodiversityActivity from './EditBiodiversityActivity';
import { useEditActivity } from './hooks/useEditActivity';
import { useEditModules } from './hooks/useEditModules';
import { useState } from 'react';
import { useAppSelector } from '@app/hooks';
import { ButtonProps } from '@components/button/Button';
import { selectCurrentProject } from '@app/features/project/projectSlice';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '@components/modal/ConfirmationModal';
import TranslatableText from '@components/translations/TranslatableText';
import AlertModal from '@components/modal/AlertModal';
import Skeleton from 'react-loading-skeleton';

export enum Tab {
  Activity = 'activity',
  Modules = 'modules'
}

const EditActivityCombined = () => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const editActivityProps = useEditActivity({ setIsSuccessModalOpen });
  const { editModulesProps, modulesInput, initialValues, values } = useEditModules({ setIsSuccessModalOpen });

  const isBiodiversity = editActivityProps.activity?.is_b_intact === true;
  // Show skeleton when there is no data yet OR when the cached activity is from a different activity (ID mismatch during navigation)
  const isLoading = editActivityProps.isFetching || (!!editActivityProps.id && editActivityProps.activity?.id !== editActivityProps.id);

  const [shouldBlockTabChange, setShouldBlockTabChange] = useState(false);
  const [tab, setTab] = useState<Tab>(Tab.Activity);
  const [tabToChange, setTabToChange] = useState<Tab | null>(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const handleTabChange = (tab: Tab) => {
    if (shouldBlockTabChange) {
      setTabToChange(tab);
      setIsConfirmationModalOpen(true);
      return;
    }
    setTab(tab);
  }

  const stayHereButton: ButtonProps = {
    onClick: () => setIsSuccessModalOpen(false),
    children: <TranslatableText translationKey="activity.stay_here" />,
    type: "button",
    className: "btn-primary"
  }

  //no need to consider public projects here (they don't allow to edit activities)
  const navigate = useNavigate();
  const { project } = useAppSelector(selectCurrentProject);
  const goToActivityListButton: ButtonProps = {
    onClick: () => {
      setIsSuccessModalOpen(false);
      navigate(`/project/${project?.id}/activities`);
    },
    children: <TranslatableText translationKey="activity.go_to_activity_list" />,
    type: "button",
    className: "btn-primary"
  }

  if (isLoading) {
    return (
      <div className="builder-main pos-relative">
        <section>
          <div className="module-group ms-3">
            <div className="pos-relative mb-2 pb-1">
              <Skeleton width={220} height={20} borderRadius={4} />
            </div>
            <div className="d-flex f-column gap-3" style={{ maxWidth: '550px' }}>
              {[100, 160, 160].map((w, i) => (
                <div key={i} className="d-flex f-column gap-8 py-1">
                  <Skeleton width={w} height={13} borderRadius={4} />
                  <Skeleton height={36} borderRadius={6} />
                </div>
              ))}
              <Skeleton width={120} height={34} borderRadius={6} style={{ marginTop: '8px' }} />
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (isBiodiversity) {
    return (
      <>
        <div className="builder-main pos-relative">
          <EditBiodiversityActivity setIsSuccessModalOpen={setIsSuccessModalOpen} />

          <AlertModal
            modalOpen={isSuccessModalOpen}
            handleCancel={() => setIsSuccessModalOpen(false)}
            headerText="activity.activity_updated"
            buttons={[stayHereButton, goToActivityListButton]}
            size='sm'
          >
            <div className="d-flex flex-column align-items-center justify-content-center p-2">
              <TranslatableText translationKey="activity.activity_saved_correctly" />
            </div>
          </AlertModal>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="builder-main pos-relative">
        {tab === Tab.Activity && <EditActivity {...editActivityProps} setShouldBlockTabs={setShouldBlockTabChange} handleTabChange={handleTabChange} />}
        {tab === Tab.Modules &&
          <EditModules {...editModulesProps}
            modulesInput={modulesInput}
            modules={[]} initialValues={initialValues}
            values={values}
            setShouldBlockTabs={setShouldBlockTabChange}
            handleTabChange={handleTabChange}
          />}

        {/* confirmation modal for unsaved changes */}
        <ConfirmationModal
          message={<TranslatableText translationKey="activity.unsaved_changes" />}
          open={isConfirmationModalOpen}
          onCancel={() => setIsConfirmationModalOpen(false)}
          onConfirm={() => {
            setIsConfirmationModalOpen(false);
            setTab(tabToChange ?? Tab.Activity);
          }}
        />

        {/* confirmation modal for success */}
        <AlertModal
          modalOpen={isSuccessModalOpen}
          handleCancel={() => setIsSuccessModalOpen(false)}
          headerText="activity.activity_updated"
          buttons={[stayHereButton, goToActivityListButton]}
          size='sm'
        >
          <div className="d-flex flex-column align-items-center justify-content-center p-2">
            <TranslatableText translationKey="activity.activity_saved_correctly" />
          </div>
        </AlertModal>
      </div>
    </>
  );
};

export default EditActivityCombined; 